import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, X, Send, Loader2, Trash2, ThumbsUp, ThumbsDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { emieSuggestionsFor } from "@/lib/guide";

// Minimal, safe inline formatter: escapes HTML, renders **bold**.
function renderContent(text) {
  const escape = (s) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = text.split("\n");
  return lines.map((line, i) => {
    const bulleted = /^\s*[-*]\s+/.test(line);
    const clean = bulleted ? line.replace(/^\s*[-*]\s+/, "") : line;
    const html = escape(clean).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    if (bulleted) {
      return (
        <div key={i} className="flex gap-2">
          <span className="mt-1 text-primary">•</span>
          <span dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      );
    }
    return <div key={i} dangerouslySetInnerHTML={{ __html: html || "&nbsp;" }} />;
  });
}

export function EmieWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const branding = useBranding();
  const assistant = branding.assistant_name || "EMIE";
  const restricted = !!user?.restricted;
  const suggestions = emieSuggestionsFor(location.pathname, restricted);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };

  const loadMessages = () => {
    fetch(`${API}/emie/messages`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        setMessages(Array.isArray(d) ? d : []);
        setLoaded(true);
        scrollToBottom();
      })
      .catch(() => setLoaded(true));
  };

  // Poll for proactive unread alerts.
  useEffect(() => {
    const check = () => {
      fetch(`${API}/emie/unread`, { credentials: "include" })
        .then((r) => r.json())
        .then((d) => setUnread(d.unread || 0))
        .catch(() => {});
    };
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, []);

  // Speak up once per session when something needs them — never nag on every poll.
  useEffect(() => {
    if (open || unread <= 0 || restricted) return;
    try {
      if (sessionStorage.getItem("gx_emie_auto_opened")) return;
    } catch {
      /* ignore */
    }
    const t = setTimeout(() => {
      if (document.querySelector('[data-testid="welcome-tour"]')) return;
      try {
        sessionStorage.setItem("gx_emie_auto_opened", "1");
      } catch {
        /* ignore */
      }
      setOpen(true);
    }, 1400);
    return () => clearTimeout(t);
  }, [unread, open, restricted]);

  useEffect(() => {
    if (open) {
      loadMessages();
      if (unread > 0) {
        fetch(`${API}/emie/mark-read`, { method: "POST", credentials: "include" })
          .then(() => setUnread(0))
          .catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming || restricted) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/emie/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg }),
      });
      if (!res.ok) {
        let detail = "EMIE is temporarily unavailable. Please try again.";
        try {
          const errBody = await res.json();
          if (typeof errBody?.detail === "string") detail = errBody.detail;
        } catch { /* keep default */ }
        if (res.status === 402 && typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("gx-subscription-expired", { detail }));
        }
        throw new Error(detail);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                role: "assistant",
                content: copy[copy.length - 1].content + payload.delta,
              };
              return copy;
            });
          } else if (payload.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: payload.error };
              return copy;
            });
          } else if (payload.done && payload.message_id) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], id: payload.message_id, rating: null };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "EMIE is temporarily unavailable. Please try again.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const clearChat = async () => {
    await fetch(`${API}/emie/messages`, { method: "DELETE", credentials: "include" });
    setMessages([]);
  };

  const rate = async (msg, rating) => {
    if (!msg.id) return;
    try {
      await api.post(`/emie/messages/${msg.id}/rate`, { rating });
      setMessages((m) => m.map((x) => (x.id === msg.id ? { ...x, rating } : x)));
    } catch (e) {
      // ignore
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 transition-transform duration-200 hover:scale-105 ${
          !open && unread > 0 ? "animate-pulse ring-2 ring-primary/40 ring-offset-2 ring-offset-background" : ""
        }`}
        aria-label="Open EMIE co-pilot"
        data-testid="emie-launcher"
      >
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
        {!open && unread > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white ring-2 ring-background"
            data-testid="emie-unread-badge"
          >
            {unread}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 flex h-[560px] w-[calc(100vw-3rem)] max-w-[400px] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
          data-testid="emie-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border bg-background/60 px-4 py-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="leading-tight">
                <p className="font-display text-sm font-bold">{assistant} Co-pilot</p>
                <p className="text-[11px] text-muted-foreground">Governance assistant</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={clearChat}
              disabled={restricted}
              aria-label="Clear conversation"
              data-testid="emie-clear"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="emie-messages">
            {messages.length === 0 && (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Sparkles className="h-6 w-6" />
                </div>
                <p className="mt-3 font-display text-sm font-bold">I'm here — ask {assistant}</p>
                <p className="mt-1 max-w-[240px] text-xs text-muted-foreground">
                  I'll watch this workspace and speak up when something needs you. Ask about the page you're on.
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      disabled={restricted}
                      className="rounded-lg border border-border bg-background px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground disabled:opacity-50"
                      data-testid="emie-suggestion"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`emie-message-${m.role}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "rounded-br-sm bg-primary text-primary-foreground"
                      : "rounded-bl-sm bg-muted text-foreground"
                  }`}
                >
                  {m.role === "assistant" && m.content === "" && streaming ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : (
                    <div className="space-y-1">{renderContent(m.content)}</div>
                  )}
                  {m.role === "assistant" && m.id && m.content && (
                    <div className="mt-2 flex items-center gap-1 border-t border-border/50 pt-2">
                      <button
                        onClick={() => rate(m, "up")}
                        className={`rounded-md p-1 transition-colors ${m.rating === "up" ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
                        data-testid={`emie-rate-up-${m.id}`}
                        aria-label="Helpful"
                      >
                        <ThumbsUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => rate(m, "down")}
                        className={`rounded-md p-1 transition-colors ${m.rating === "down" ? "text-destructive" : "text-muted-foreground hover:text-foreground"}`}
                        data-testid={`emie-rate-down-${m.id}`}
                        aria-label="Not helpful"
                      >
                        <ThumbsDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-border p-3"
          >
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder={restricted ? "Read-only for now — continue with a plan to ask EMIE" : "Ask about your data…"}
                disabled={restricted}
                className="max-h-28 flex-1 resize-none rounded-xl border border-input bg-background px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary"
                data-testid="emie-input"
              />
              <Button
                type="submit"
                size="icon"
                disabled={streaming || restricted || !input.trim()}
                className="h-10 w-10 shrink-0 rounded-xl"
                data-testid="emie-send"
              >
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
